import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { PageHeader } from "../../components/ui/page-header";
import { useAdminAdjustCredits, useAdminUser } from "../../api/hooks";
import type { CreditLedgerEntry } from "../../api/types";

/** Admin: full detail for one user — profile, credits + ledger, groups, and a
 *  credit-adjustment form. */
export function AdminUserDetailPage(): ReactNode {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useAdminUser(userId);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title={data?.user.displayName ?? "User"}
        description={data?.user.email}
        back={{ label: "Back to users", onClick: () => navigate("/admin") }}
      />

      {isError ? (
        <Card className="p-6 text-body-md text-error">
          Couldn&apos;t load this user.
        </Card>
      ) : isLoading || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <CreditsCard
            userId={userId!}
            balance={data.credits.balance}
            ledger={data.credits.ledger}
          />
          <GroupsCard groups={data.groups} />
          <ProfileCard
            id={data.user.id}
            createdAt={data.user.createdAt}
            preferredCurrency={data.user.preferredCurrency}
            isTestAccount={data.user.isTestAccount}
          />
        </div>
      )}
    </div>
  );
}

function CreditsCard({
  userId,
  balance,
  ledger,
}: {
  userId: string;
  balance: number;
  ledger: CreditLedgerEntry[];
}): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Credits</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <p className="text-display-sm text-on-surface">{balance}</p>
          <p className="text-body-sm text-on-surface-variant">
            current balance
          </p>
        </div>

        <AdjustCreditsForm userId={userId} />

        <div>
          <p className="mb-2 text-label-md text-on-surface-variant">
            Recent ledger
          </p>
          {ledger.length === 0 ? (
            <p className="text-body-sm text-on-surface-variant">
              No transactions yet.
            </p>
          ) : (
            <ul className="divide-y divide-outline-variant/30">
              {ledger.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-body-sm text-on-surface">
                      {entry.type}
                    </p>
                    {entry.reason ? (
                      <p className="truncate text-body-sm text-on-surface-variant">
                        {entry.reason}
                      </p>
                    ) : null}
                    <p className="text-body-sm text-on-surface-variant">
                      {new Date(entry.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={
                      entry.delta >= 0
                        ? "text-label-md text-primary"
                        : "text-label-md text-error"
                    }
                  >
                    {entry.delta >= 0 ? "+" : ""}
                    {entry.delta}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AdjustCreditsForm({ userId }: { userId: string }): ReactNode {
  const adjust = useAdminAdjustCredits(userId);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const parsed = Number(delta);
    if (!Number.isInteger(parsed) || parsed === 0) return;
    await adjust.mutateAsync({
      delta: parsed,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    });
    setDelta("");
    setReason("");
  }

  const parsed = Number(delta);
  const invalid = delta === "" || !Number.isInteger(parsed) || parsed === 0;

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-3 rounded-xl bg-surface-container p-4"
    >
      <p className="text-label-md text-on-surface">Adjust balance</p>
      <div className="flex gap-3">
        <div className="w-28 space-y-1">
          <Label htmlFor="delta">Delta</Label>
          <Input
            id="delta"
            type="number"
            inputMode="numeric"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="±"
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label htmlFor="reason">Reason (optional)</Label>
          <Input
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={280}
            placeholder="e.g. goodwill credit"
          />
        </div>
      </div>
      {adjust.error ? (
        <p className="text-body-sm text-error">
          Adjustment failed (the balance can&apos;t go below zero).
        </p>
      ) : null}
      <Button type="submit" disabled={invalid || adjust.isPending}>
        {adjust.isPending ? "Applying…" : "Apply adjustment"}
      </Button>
    </form>
  );
}

function GroupsCard({
  groups,
}: {
  groups: { id: string; label: string; currency: string; role: string }[];
}): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Groups ({groups.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? (
          <p className="text-body-sm text-on-surface-variant">
            Not a member of any group.
          </p>
        ) : (
          <ul className="divide-y divide-outline-variant/30">
            {groups.map((group) => (
              <li
                key={group.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <span className="truncate text-body-md text-on-surface">
                  {group.label}
                </span>
                <span className="shrink-0 text-label-sm text-on-surface-variant">
                  {group.role} · {group.currency}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ProfileCard({
  id,
  createdAt,
  preferredCurrency,
  isTestAccount,
}: {
  id: string;
  createdAt: string;
  preferredCurrency?: string;
  isTestAccount: boolean;
}): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-body-sm">
        <Row label="User ID" value={id} mono />
        <Row label="Joined" value={new Date(createdAt).toLocaleDateString()} />
        <Row label="Preferred currency" value={preferredCurrency ?? "—"} />
        {/* Called out because the user list hides these by default, so landing
            here from a direct link is the one way to meet one unlabelled. */}
        {isTestAccount ? <Row label="Test account" value="Yes" /> : null}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-on-surface-variant">{label}</span>
      <span
        className={
          mono ? "font-mono text-xs text-on-surface" : "text-on-surface"
        }
      >
        {value}
      </span>
    </div>
  );
}
